import { DocumentSignerState } from 'src/@types/documents/types';
import { DocumentSignatureType } from 'src/@types/documents/enums/documentSignatureTypes';

type SigningTabVisibility = DocumentSignerState['showTabs'];
type SigningTabName = keyof SigningTabVisibility;

const ALL_SIGNING_TABS_HIDDEN: SigningTabVisibility = {
  MOBILE_ID: false,
  SMART_ID: false,
  EPARAKSTS: false,
  EPARAKSTS_CARD: false,
  APPROVE: false,
  ACKNOWLEDGE: false,
  ENDORSE: false,
  SIGNATURE_PAD: false,
  EID_EASY: false,
};

const QUALIFIED_SIGNATURE_TYPES = [
  DocumentSignatureType.MobileId,
  DocumentSignatureType.SmartId,
  DocumentSignatureType.EParaksts,
  DocumentSignatureType.EidEasy,
];

const UNQUALIFIED_SIGNATURE_TYPES = [
  DocumentSignatureType.Unqualified,
  DocumentSignatureType.Handwritten,
];

const QUALIFIED_SIGNING_TABS: SigningTabName[] = [
  'MOBILE_ID',
  'SMART_ID',
  'EPARAKSTS',
  'EPARAKSTS_CARD',
  'EID_EASY',
];

const UNQUALIFIED_SIGNING_TABS: SigningTabName[] = ['APPROVE', 'SIGNATURE_PAD'];

export type SigningMethodAvailability = {
  isEidEasyAvailable: boolean;
  isSignaturePadAvailable: boolean;
};

export type AllowedSigningTabs = {
  initialTab: SigningTabName;
  showTabs: SigningTabVisibility;
};

const signingTabsForSignatureType = (
  signatureType: DocumentSignatureType
): SigningTabName[] | null => {
  if (QUALIFIED_SIGNATURE_TYPES.includes(signatureType)) {
    return QUALIFIED_SIGNING_TABS;
  }

  if (UNQUALIFIED_SIGNATURE_TYPES.includes(signatureType)) {
    return UNQUALIFIED_SIGNING_TABS;
  }

  return null;
};

const isSigningTabAvailable = (tab: SigningTabName, availability: SigningMethodAvailability) => {
  if (tab === 'EID_EASY') {
    return availability.isEidEasyAvailable;
  }

  if (tab === 'SIGNATURE_PAD') {
    return availability.isSignaturePadAvailable;
  }

  return true;
};

export function requestedSignatureType(
  signatureType: DocumentSignatureType | null | undefined,
  availability: SigningMethodAvailability
): AllowedSigningTabs | null {
  if (signatureType === null || signatureType === undefined) {
    return null;
  }

  const allowedTabs = signingTabsForSignatureType(signatureType)?.filter((tab) =>
    isSigningTabAvailable(tab, availability)
  );

  if (!allowedTabs?.length) {
    return null;
  }

  return {
    initialTab: allowedTabs[0],
    showTabs: allowedTabs.reduce(
      (visibility, tab) => ({ ...visibility, [tab]: true }),
      ALL_SIGNING_TABS_HIDDEN
    ),
  };
}
